import React, { useEffect, useState } from 'react';
import Modal from '../components/Modal';

const CredoraxChallenge = ({ challengeDetails, setLoading, onCompletion }) => {
	const [challengeType, setChallengeType] = useState(challengeDetails?.challengeType);
	const [challengeUrl, setChallengeUrl] = useState(challengeDetails?.challengeUrl);
	const [threeDSServerTransID, setThreeDSServerTransID] = useState(challengeDetails?.threeDSServerTransID);
	const [gearOrderId, setGearOrderId] = useState(challengeDetails?.gearOrderId);

	/**
	 * Create an event listener to handle messages from the challenge iframes.
	 */
	useEffect(() => {
		const messageHandler = (event) => {
			// Check origin against process.env for local development and against window.location for deployed environments.
			if (event.origin !== window.location.origin && event.origin !== process.env.REACT_APP_GEAR_REST_URL) {
				return;
			}

			// Make sure this is the 3DSecure message
			if (!event.data.threeDSecure) {
				return;
			}

			/**
			 * Decode the base64 encoded JSON message and check if the transaction requires the
			 * one-time passcode challenge.
			 * If so, set the challenge details to display the challenge iframe.
			 * If not, call the onCompletion callback with the response.
			 */
			const response = JSON.parse(atob(event.data.threeDSecure));
			if (response?.result === 'TRANSACTION_PENDING_CARDHOLDER_AUTH') {
				let { challengeType, challengeUrl, threeDSServerTransID, gearOrderId } = response || {};
				setChallengeType(challengeType);
				setChallengeUrl(challengeUrl);
				setThreeDSServerTransID(threeDSServerTransID);
				setGearOrderId(gearOrderId);
			} else {
				onCompletion(response);
			}
		};

		window.addEventListener('message', messageHandler);

		return () => {
			window.removeEventListener('message', messageHandler);
		};
	}, [onCompletion, setLoading]);

	/**
	 * If the challenge type is dfp, create a hidden form and submit it to the challenge URL.
	 * The form target is set to a hidden iframe so the form's response will be received in the iframe.
	 */
	useEffect(() => {
		let form, timer;
		if (challengeType === 'dfp') {
			setLoading(true);

			let notificationURL = `${process.env.REACT_APP_GEAR_REST_URL}/3DSecure/${gearOrderId}/dfp`;
			if (notificationURL.startsWith('/')) {
				notificationURL = window.location.origin + notificationURL;
			}
			var encodedJson = window.btoa(
				JSON.stringify({
					threeDSMethodNotificationURL: notificationURL,
					threeDSServerTransID: threeDSServerTransID
				})
			);

			form = document.createElement('form');
			form.target = '3dChallengeFrame';
			form.method = 'post';
			form.action = challengeUrl;

			const input = document.createElement('input');
			input.type = 'hidden';
			input.name = 'threeDSMethodData';
			input.value = encodedJson;
			form.appendChild(input);

			document.body.appendChild(form);
			form.submit();

			/** If something goes wrong inside the iFrame we won't know about it. Set up a timer to fail the challenge after 15 seconds. */
			timer = setTimeout(() => {
				onCompletion({ result: 'TRANSACTION_TIMEOUT', resultText: '3D Secure challenge timed out' });
			}, 15000);
		} else {
			setLoading(false);
		}

		return () => {
			form && form.remove();
			timer && clearTimeout(timer);
		};
	}, [challengeType, challengeUrl, threeDSServerTransID, gearOrderId, setLoading, onCompletion]);

	if (!challengeType) {
		return null;
	}

	if (challengeType === 'dfp') {
		return (
			<iframe
				title="3dChallengeFrame"
				name="3dChallengeFrame"
				id="3dChallengeFrame"
				style={{ display: 'none' }}
			></iframe>
		);
	}

	const onClose = () => {
		onCompletion({ result: 'RESET' });
		return true;
	};

	const modalStyles = {
		displayProps: {
			modalHeader: { style: { all: 'unset', padding: '1rem 1.5rem' } },
			modalContent: { style: { borderRadius: '20px' } }, // Round corners
			modalBody: { className: 'modal-body text-center', style: { padding: '0' } }
		}
	};

	return (
		<>
			<Modal
				{...modalStyles}
				doConfirm={onClose}
			>
				<div id="3dChallengeArea">
					<div id="3DSecureHelp" style={{ paddingInline: '1rem' }}>
						<h2 className="mb-3 mt-0">Additional Security Required</h2>
						<p className="small" style={{ textWrapStyle: 'pretty' }}>
							To increase the security of online transactions, card issuers have introduced 3D Secure. You
							have chosen a card that participates with 3D secure so you will need to authenticate
							yourself with your bank. In the area below you will be interacting directly with your bank,
							please complete your payment by using their form.
						</p>
						<hr />
					</div>
					<iframe
						title="3D Secure Challenge"
						src={challengeUrl}
						height="400"
						style={{ width: '100%' }}
					></iframe>
				</div>
			</Modal>
		</>
	);
};

export default CredoraxChallenge;
